import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Filter,
  Table,
  TableContainer,
  TableProps,
} from '@odin-labs/components';
import { navigateTo } from 'utils';
import { paginationSizePerPage as limit } from 'utils/constants';
import { useGetJobsiteContainerWorkersQuery } from 'apollo/generated/client-operations';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { LoadingError } from 'components/loadingError';
import { JobsiteTabProps } from 'containers/jobsite/types';
import {
  JobsiteWorker,
  JobsiteWorkerColumn,
  JobsiteWorkersFilters,
} from './types';
import { getColumns, getFilterItems } from './tables';

export function JobsiteWorkersTabContainer(
  props: JobsiteTabProps,
): ReactElement {
  const { jobsiteId, onTabApiChange } = props;
  const history = useHistory();
  const { page, search, updateUrl } = usePageQueryParams();
  const offset = page * limit;

  const { data, loading, error, refetch } = useGetJobsiteContainerWorkersQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      jobsiteId,
      jobsiteJobsiteWorkersInput: {
        search,
        paginationInput: { limit, offset },
      },
    },
  });

  const jobsite = data?.getJobsite;

  const columns = React.useMemo<JobsiteWorkerColumn[]>(() => getColumns(), []);

  const filterItems = React.useMemo(() => getFilterItems({ search }), [search]);

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({
      page: pageIndex ? pageIndex + 1 : null,
    });
  };

  const onFiltersChangeHandler = (
    changedFilters: Partial<JobsiteWorkersFilters>,
  ): void => {
    updateUrl({ page: null, ...changedFilters });
  };

  const onRowClickHandler: TableProps<JobsiteWorker>['onRowClick'] = ({
    data: jobsiteWorker,
    event,
  }): void => {
    const path = `/worker/${jobsiteWorker?.contractorWorker?.worker?.workerId}`;
    navigateTo(path, history, event);
  };

  const workers = jobsite?.jobsiteWorkers.edges.map((c) => c.node) ?? [];
  const workersCount = jobsite?.jobsiteWorkers?.count ?? 0;
  const pageCount = Math.ceil(workersCount / limit);

  React.useEffect(() => onTabApiChange({ refetchData: refetch }), [refetch]);

  if (error) {
    return <LoadingError error={error} />;
  }

  return (
    <TableContainer className="jobsite-workers-container">
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFiltersChangeHandler}
      />
      <Table
        loading={loading}
        columns={columns}
        data={workers}
        initialState={{ pageSize: limit }}
        pageCount={pageCount}
        pageIndex={page}
        remote
        enablePagination
        onRowClick={onRowClickHandler}
        onPageChange={onPageChangeHandler}
        disableGlobalFilter
      />
    </TableContainer>
  );
}
